import { apiClient } from '@/auth';

/**
 * Wire shape of `GET /patients/{id}/emotion` (MNT swagger
 * `PatientEmotionResponse`). The `emotion` string is the OPTION CODE
 * the patient picked — matches one of the values in
 * `EmotionQuestion.options[]`. Not a 1-10 numeric level (we had that
 * wrong in the earlier port; the chart silently rendered empty because
 * every `emotion_level` read came back undefined).
 */
export type Emotion = {
  id: number;
  patient_id: number;
  /** Calendar date in YYYY-MM-DD. */
  emotion_date: string;
  /** Time of day, hh:mm:ss. Nullable for date-only entries. */
  emotion_time: string | null;
  /** Combined date+time ISO string — usable for sorting. */
  emotion_date_time: string;
  /** The clinic-configured question text at log time. Snapshot of
   *  `EmotionQuestion.question` at the moment the patient answered. */
  question: string | null;
  /** The option code the patient chose — matches one of
   *  `EmotionQuestion.options[]`. */
  emotion: string;
  note?: string | null;
};

/**
 * Per MNT swagger:18782 (`EmotionQuestionResponse`). Each clinic can
 * customize the question shown to the patient — defaults to an emotion
 * scale, but some clinics use it to track bowel movements / digestive
 * symptoms / etc. The clinician UI uses the question text to label the
 * tab content appropriately ("Emotion" vs "Digestion").
 */
export type EmotionQuestion = {
  question: string;
  options: string[];
};

export const emotionsApi = {
  async list(patientId: number, params?: { from?: string; to?: string }): Promise<Emotion[]> {
    try {
      // MNT endpoint is singular `/emotion` (per swagger
      // `appApiEmotionGetEmotions`). Pluralizing it returns 404 — this
      // had been silently breaking the mood-trend chart.
      const res = await apiClient.get<Emotion[]>(`/patients/${patientId}/emotion`, {
        params: {
          date_since: params?.from,
          date_until: params?.to,
        },
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      // Some clinics' patients legitimately have no logged emotions and
      // the server returns 404 instead of 200/[]. Swallow 4xx and return
      // an empty list so the mood trend renders as "no data" rather
      // than breaking the entire Wellbeing tab.
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status !== undefined && status >= 400 && status < 500) return [];
      throw err;
    }
  },

  /**
   * Patient-level "what question are we asking?" — drives the dynamic
   * label derivation. Returns null on 4xx so the tab can fall back to the
   * default "Emotion" label without erroring.
   */
  async question(patientId: number): Promise<EmotionQuestion | null> {
    try {
      const res = await apiClient.get<EmotionQuestion>(`/patients/${patientId}/emotion_question`);
      return res.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status !== undefined && status >= 400 && status < 500) return null;
      throw err;
    }
  },
};
